// src/components/help/HelpMarkdownRenderer.jsx
// Shared markdown renderer for help articles.
// Reuses the component map pattern from GenericSection.tsx.
// Preprocesses :::if / :::endif conditional blocks based on user context.

import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useFeatureFlags } from '@/contexts/FeatureContext';
import { useUser } from '@/contexts/UserContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useHelpPanel } from '@/contexts/HelpContext';
import { useHelp } from '@/hooks/useHelp';
import { processConditionalBlocks } from '@/utils/helpConditionalBlocks';

const baseComponents = {
  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-3 text-gray-900" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-xl font-semibold mt-5 mb-2 text-gray-900" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-800" {...props} />,
  h4: ({ node, ...props }) => <h4 className="text-base font-semibold mt-3 mb-1 text-gray-800" {...props} />,
  p: ({ node, ...props }) => <p className="my-2 text-gray-600 leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="text-gray-600" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-primary/30 bg-primary/5 pl-4 py-2 my-3 rounded-r-md" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold text-gray-800" {...props} />,
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-3">
      <table className="min-w-full divide-y divide-gray-200 text-sm" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="px-3 py-2 text-left font-semibold text-gray-700 bg-gray-50" {...props} />,
  td: ({ node, ...props }) => <td className="px-3 py-2 text-gray-600 border-t border-gray-100" {...props} />,
  img: ({ node, ...props }) => (
    <img className="max-w-full rounded-lg shadow-sm border border-gray-200 my-3" {...props} />
  ),
  code: ({ node, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const isInline = !match && !(children?.toString().includes('\n'));
    return isInline
      ? <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm" {...props}>{children}</code>
      : <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto my-3 text-sm"><code {...props}>{children}</code></pre>;
  },
  hr: ({ node, ...props }) => <hr className="my-6 border-gray-200" {...props} />,
};

export function HelpMarkdownRenderer({ content, onArticleClick }) {
  const { solutions } = useFeatureFlags();
  const { user } = useUser();
  const { can } = usePermissions();
  const { currentArticle, openArticle } = useHelpPanel();
  const { getArticle, articles } = useHelp();

  const processedContent = useMemo(() => {
    if (!content) return '';

    const ctx = {
      solutions,
      billingMode: user?.organization?.settings?.billing_mode || 'cash',
      can,
      orgSettings: user?.organization?.settings || {},
    };

    return processConditionalBlocks(content, ctx);
  }, [content, solutions, user, can]);

  // Build components with article-aware link handling
  const components = useMemo(() => ({
    ...baseComponents,
    a: ({ node, href, children, ...props }) => {
      // Internal article link: no protocol, no leading /, no #, no mailto
      if (href && !href.startsWith('http') && !href.startsWith('/') && !href.startsWith('#') && !href.startsWith('mailto:')) {
        return (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              // Strip ../ prefixes to get clean category/slug
              const cleanHref = href.replace(/^(\.\.\/)+/, '');
              let target = null;
              if (cleanHref.includes('/')) {
                // Explicit category/slug format
                const [cat, slug] = cleanHref.split('/');
                target = getArticle(cat, slug);
              } else {
                // Same-category slug reference
                const category = currentArticle?.category;
                if (category) {
                  target = getArticle(category, cleanHref);
                }
                // Fallback: search all articles by slug
                if (!target) {
                  target = articles.find(a => a.slug === cleanHref) || null;
                }
              }
              if (target) {
                onArticleClick ? onArticleClick(target) : openArticle(target);
              }
            }}
            className="text-primary hover:text-primary/80 underline cursor-pointer bg-transparent border-0 p-0 font-inherit text-inherit inline"
          >
            {children}
          </button>
        );
      }
      return (
        <a className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer" href={href} {...props}>
          {children}
        </a>
      );
    },
  }), [currentArticle, getArticle, articles, openArticle, onArticleClick]);

  if (!processedContent) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={components}
    >
      {processedContent}
    </ReactMarkdown>
  );
}
